import React, { useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
// 아이콘
import CoreSvg from './CoreSvg';
import Icon from './icons';

type CheckType = 'common' | 'button';

interface ICheckBox {
	check_type: CheckType;
	value?: string;
	name: string;
	checked_value: boolean;
	_onMouseDown?: () => void;
	_color?: string;
	_margin?: string;
	_disabled?: boolean;
}

const CoreCheckBox = ({
	check_type = 'common',
	value,
	name = 'group',
	checked_value,
	_onMouseDown = () => undefined,
	_color,
	_margin,
	_disabled = false,
}: ICheckBox) => {
	// checkbox 전체를 감싸고 있는 label태그 DOM
	const check_input_ref = useRef<HTMLInputElement>(null);

	// 체크 되었는지 여부
	const [focused, setFocused] = useState(false);

	const theme = useTheme();

	return (
		<CheckBoxLabel
			check_type={check_type}
			is_checked={checked_value}
			onMouseDown={() => {
				if (_disabled) return;
				_onMouseDown();
			}}
			focused={focused}
			_margin={_margin}
			_disabled={_disabled}
		>
			<CheckBoxInput
				type="checkbox"
				ref={check_input_ref}
				value={value}
				name={name}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				onClick={() => setFocused(false)}
				onKeyDown={(e) => {
					if (e.key === ' ') {
						if (_disabled) return;
						_onMouseDown();
					}
				}}
				disabled={_disabled}
			/>
			<CoreSvg
				_fill={
					_disabled
						? theme.colors.gray.gray30
						: checked_value
						? _color ?? theme.colors.primary.primary50_key
						: theme.colors.gray.gray80_text
				}
			>
				{checked_value ? <Icon.CheckBoxOn /> : <Icon.CheckBoxOff />}
			</CoreSvg>
			{value}
		</CheckBoxLabel>
	);
};

const CheckBoxLabel = styled.label<{
	check_type: 'common' | 'button';
	is_checked: boolean;
	focused: boolean;
	_margin?: string;
	_disabled?: boolean;
}>`
	width: ${({ check_type }) => (check_type === 'button' ? '100%' : 'min-content')};
	white-space: nowrap;
	color: ${(props) => props.theme.colors.gray.gray80_text};
	${({ theme }) => theme.typo.body.p};
	background-color: transparent;

	${({ theme }) => theme.common.flexStart};
	cursor: pointer;
	transition: 0.2s;

	${({ focused }) =>
		focused &&
		`
		outline: -webkit-focus-ring-color auto 1px;
	`}

	${({ theme }) => theme.typo.body.p};

	${({ check_type, theme }) =>
		check_type === 'button' &&
		`
			height: 56px;
			padding: 0 24px 0 16px;
			border: 1px solid ${theme.colors.gray.gray15};
			border-radius: 8px;

			${theme.typo.body.p_bold};
		`}
	${({ check_type, is_checked, theme }) =>
		check_type === 'button' &&
		is_checked &&
		`
			background-color: ${theme.colors.primary.primary10_lighten};
		`}
	& > span {
		margin-right: 8px;
	}

	${({ _margin }) => _margin && `margin: ${_margin}`};

	${({ _disabled, theme }) =>
		_disabled &&
		`
		border-color: ${theme.colors.gray.gray15};
		background-color: ${theme.colors.gray.gray15};
		color: ${theme.colors.gray.gray30}; 
		cursor: default;
	`}
`;

const CheckBoxInput = styled.input`
	width: 0;
	height: 0;
	display: none;
`;

export default CoreCheckBox;

// props
// check_type: common, button (피그마 참고)
// value: 라벨 및 값이 되는 텍스트
// !!name!!: 다른 checkbox를 같은 그룹으로 묶기 위한 것으로, 같은 그룹은 같은 값을 입력한다.
//
// 값을 상위 컴포넌트로 옯기기 위한 dispatch가 필요할 것으로 보인다.
